import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { types, customerPortal } from "@vilocnv/allsetra-core";
import { useAppDispatch, useAppSelector } from "hooks";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { selectDrawerSelectedAccountId } from "app/data/selectors/dashboardSelectors";
import { selectGeozonesState } from "domain/geozones/store/selectors/geozones.selector";
import { getGeozoneByIdAction } from "domain/geozones/store/actions/get-geozone-by-id.action";
import { getGeozoneObjectsAction } from "domain/geozones/store/actions/get-geozone-objects.action";
import {
  clearSelectedObject,
  setSelectedObject
} from "domain/objects/store/objects.slice";
import getObjectDataForMapAction from "domain/objects/store/actions/get-object-data-for-map.action";
import { selectObjectsState } from "domain/objects/store/selectors/objects.selector";

type ObjectForMapRequestDto = customerPortal.objects.ObjectForMapRequestDto;

const { geozones } = customerPortal;

const useGeozoneDetailPage = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id: geozoneId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const [mounted, setMounted] = useState(false);
  const [mapRef, setMapRef] = useState<google.maps.Map>();
  const [showMarkers, setShowMarkers] = useState(false);
  const [selectedTooltipMarker, setSelectedTooltipMarker] =
    useState<types.MarkerType>();
  const {
    selectedGeozone,
    isLoadingSelectedGeozone,
    geozoneObjects,
    isLoadingGeozoneObjects,
    geozoneObjectsMarkers
  } = useAppSelector(selectGeozonesState);
  const { markers, selectedObject } = useAppSelector(selectObjectsState);

  const accountId = useAppSelector(selectDrawerSelectedAccountId);

  const searchQuery = searchParams.get("search");
  const filteredGeozonesObjects: Array<types.IObject> = useMemo(() => {
    if (!geozoneObjects || geozoneObjects.length === 0) return [];
    if (!searchQuery) return geozoneObjects;

    return geozoneObjects.filter((geozoneObject: types.IObject) =>
      geozoneObject.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  }, [searchQuery, geozoneObjects]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (!accountId) return;
    if (!geozoneId) return;

    getGeozoneById(accountId, geozoneId);
    getGeozoneObjects(accountId, geozoneId);
  }, [mounted, accountId, geozoneId]);

  useEffect(() => {
    if (!selectedGeozone) return;
    if (!mapRef) return;

    geozones.zoomToGeozone(mapRef, selectedGeozone);
  }, [selectedGeozone, mapRef]);

  const getGeozoneById = (accountId: string, geozoneId: string) => {
    const requestData = {
      accountId,
      geozoneId
    };
    dispatch(getGeozoneByIdAction(requestData));
  };

  const getGeozoneObjects = (accountId: string, geozoneId: string) => {
    const requestData = {
      accountId,
      geozoneId
    };
    dispatch(getGeozoneObjectsAction(requestData));
  };

  const handleMapLoad = (map: google.maps.Map) => {
    if (map) {
      setMapRef(map);
    }
  };

  const toggleShowMarkers = () => {
    setShowMarkers(!showMarkers);
  };

  const handleMarkerMouseOver = (uniqueId: string) => {
    setSelectedTooltipMarker(undefined);
    const marker = markers.find((m) => m?.uniqueId === uniqueId);
    if (marker && marker.position && marker.name) {
      setSelectedTooltipMarker(marker);
    }
  };

  const handleMarkerMouseOut = () => {
    setSelectedTooltipMarker(undefined);
  };

  const handleShowInfoWindowClick = (uniqueId: string) => {
    if (!uniqueId) return;

    dispatch(setSelectedObject({ uniqueId }));

    const requestData: ObjectForMapRequestDto = {
      accountId: accountId ?? "",
      objectId: uniqueId
    };
    dispatch(getObjectDataForMapAction(requestData));
  };

  const handleCloseInfoWindowClick = () => {
    dispatch(clearSelectedObject());
  };

  const onEditGeozoneClick = (geozone: types.IGeozone) => {
    navigate(`/dashboard/geozone/edit/${geozone.uniqueId}`);
  };

  const handleObjectClick = (objectId: string) => {
    navigate(`/dashboard/objects/details/${objectId}`);
  };

  return {
    markers,
    geozoneObjectsMarkers,
    selectedObject,
    selectedGeozone,
    isLoadingSelectedGeozone,
    geozoneObjects: filteredGeozonesObjects,
    isLoadingGeozoneObjects,
    translator: t,
    navigate,
    theme,
    selectedTooltipMarker,
    onEditGeozoneClick,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleShowInfoWindowClick,
    handleCloseInfoWindowClick,
    showMarkers,
    toggleShowMarkers,
    handleMapLoad,
    handleObjectClick
  };
};

export default useGeozoneDetailPage;
